import {
  getBlogPosts,
  GetBlogPostsResponse,
  getNewsPage,
  createNewsPageQuery,
  HoldingNewsPageResponse,
  imageUrlFor,
} from '@klokgroep/sanity';
import { BlogsContainer } from '@klokgroep/shared-components/src/BlogsContainer';
import { Filters, FilterSection } from '@klokgroep/shared-components/src/Filters';
import {
  getPropertiesWithPreviewAndSitewideData,
  PropertiesWithPreviewNavigationAndSocialsAndMetaData,
} from '@klokgroep/shared-components/utils/getPropertiesWithPreviewNavigationAndSocials';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { MaxWidth } from '@klokgroep/shared-components/src/MaxWidth';
import { BlockContainerWithBackgroundProperties } from '@klokgroep/shared-components/src/BlockContainerWithBackground';
import { PageHero } from '@klokgroep/shared-components/src/PageHero';
import { Pagination } from '@klokgroep/shared-components/src/Pagination';
import { RichText } from '@klokgroep/shared-components/src/RichText';
import { useMemo, Fragment, useCallback, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import styles from './index.module.css';
import { getRevalidateInterval } from '@klokgroep/shared-components/utils/getRevalidateInterval';
import { getOptionsFromBlogPosts } from '@klokgroep/shared-components/utils/getOptionsFromBlogPosts';
import { getCorporateSiteIdPaths } from 'utils/hostnames';
import { useSiteInfo } from '@klokgroep/shared-components/src/SiteInfoProvider';
import { useCommonTranslations } from '@klokgroep/shared-components/utils/useTranslations';

interface Properties extends PropertiesWithPreviewNavigationAndSocialsAndMetaData<HoldingNewsPageResponse> {
  blogPosts?: GetBlogPostsResponse[];
}

const BACKGROUND_COLORS = {
  ['vandeklok' as const]: 'white',
  ['novaform' as const]: 'black',
  ['vanerk' as const]: 'white',
  ['heilijgers' as const]: 'white',
};

const ITEMS_PER_PAGE = 9;

const NewsOverviewPage: NextPage<Properties> = ({ pageHero, blogPosts, newsletterSubscriptionBlock }) => {
  const blogContainerReference = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const t = useCommonTranslations();

  const activePage = useMemo(() => Number.parseInt(router.query?.page?.toString() || '1'), [router.query.page]);

  const selectedCategories = useMemo(
    () => (router.query.categories ? router.query.categories.toString().split(',') : []),
    [router.query.categories]
  );

  const selectedSpecialisms = useMemo(
    () => (router.query.specialisms ? router.query.specialisms.toString().split(',') : []),
    [router.query.specialisms]
  );

  const categories = useMemo(
    () => getOptionsFromBlogPosts(blogPosts, 'categories', selectedCategories),
    [blogPosts, selectedCategories]
  );

  const specialisms = useMemo(
    () => getOptionsFromBlogPosts(blogPosts, 'specialisms', selectedSpecialisms),
    [blogPosts, selectedSpecialisms]
  );

  const onCategoryChange = useCallback(
    (value: string) => {
      const newCategories = selectedCategories.includes(value)
        ? selectedCategories.filter((type) => type !== value)
        : [...selectedCategories, value];

      const query = { ...router.query, page: 1, categories: newCategories };

      router.replace({ query }, { pathname: router.asPath.split('?')[0], query }, { shallow: true });
    },
    [router, selectedCategories]
  );

  const onSpecialismChange = useCallback(
    (value: string) => {
      const newSpecialisms = selectedSpecialisms.includes(value)
        ? selectedSpecialisms.filter((type) => type !== value)
        : [...selectedSpecialisms, value];

      const query = { ...router.query, page: 1, specialisms: newSpecialisms };
      router.replace({ query }, { pathname: router.asPath.split('?')[0], query }, { shallow: true });
    },
    [router, selectedSpecialisms]
  );

  const filteredBlogPosts = useMemo(() => {
    let newBlogPosts = [...(blogPosts || [])];

    if (selectedCategories.length > 0) {
      newBlogPosts = newBlogPosts.filter(({ categories }) =>
        categories?.some((category) => category?.slug?.current && selectedCategories.includes(category?.slug?.current))
      );
    }
    if (selectedSpecialisms.length > 0) {
      newBlogPosts = newBlogPosts.filter(({ specialisms }) =>
        specialisms?.some(
          (specialism) => specialism?.slug?.current && selectedSpecialisms.includes(specialism?.slug?.current)
        )
      );
    }

    return newBlogPosts;
  }, [blogPosts, selectedCategories, selectedSpecialisms]);

  const filteredPaginatedBlogPosts = useMemo(
    () => filteredBlogPosts.slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE),
    [activePage, filteredBlogPosts]
  );

  const totalPages = useMemo(() => Math.ceil(filteredBlogPosts.length / ITEMS_PER_PAGE), [filteredBlogPosts.length]);

  useEffect(() => {
    if (!blogContainerReference.current?.offsetTop) {
      return;
    }

    const top = blogContainerReference.current.offsetTop - 100;

    if (window.scrollY > top) {
      window.scrollTo({ left: 0, top, behavior: 'smooth' });
    }
  }, [activePage]);
  const { theme } = useSiteInfo();

  const backgroundColor = theme
    ? (BACKGROUND_COLORS[
        theme as keyof typeof BACKGROUND_COLORS
      ] as BlockContainerWithBackgroundProperties['backgroundColor'])
    : 'white';

  return (
    <Fragment>
      <PageHero {...pageHero} backgroundColor={backgroundColor} />
      <div className={styles.generalStylesContainer}>
        <MaxWidth>
          <div className={styles.container}>
            <div className={styles.filterContainer}>
              <Filters title={t('news_overview_filters')}>
                <FilterSection
                  title={t('news_overview_filters_categories')}
                  items={categories}
                  onChange={onCategoryChange}
                />
                <FilterSection
                  title={t('news_overview_filters_specialisms')}
                  items={specialisms}
                  onChange={onSpecialismChange}
                />
              </Filters>
            </div>
            <div className={styles.blogContainer} ref={blogContainerReference}>
              {filteredPaginatedBlogPosts?.length > 0 ? (
                <BlogsContainer
                  items={filteredPaginatedBlogPosts}
                  backgroundColor={backgroundColor}
                  newsletterSubscriptionBlock={newsletterSubscriptionBlock}
                  title={t('news_overview_filters_latest_news_corporates')}
                />
              ) : (
                <RichText backgroundColor="lightGray">
                  <p>{t('news_overview_no_articles_found')}</p>
                </RichText>
              )}
            </div>
          </div>
          {totalPages > 1 ? (
            <Pagination activePage={activePage} query={router.query} totalPages={totalPages} />
          ) : undefined}
        </MaxWidth>
      </div>
    </Fragment>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = getCorporateSiteIdPaths();
  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<Properties, Record<string, any>, { token?: string }> = async ({
  preview = false,
  params,
  previewData,
}) => {
  const newsPage = await getNewsPage(preview, params?.siteId);

  if (!newsPage) {
    return { notFound: true, revalidate: getRevalidateInterval() };
  }

  const blogPosts = await getBlogPosts(preview, { siteIds: [params?.siteId], filterNoIndex: true });
  const dataForPreview = { query: createNewsPageQuery(params?.siteId), token: previewData?.token };

  const properties = await getPropertiesWithPreviewAndSitewideData(
    preview,
    dataForPreview,
    { ...newsPage, blogPosts },
    {
      description: newsPage?.seo?.pageSeo?.description,
      image:
        newsPage?.seo?.pageSeo?.image ||
        (newsPage.pageHero?.image ? imageUrlFor(newsPage.pageHero?.image).url() : undefined),
      title: newsPage?.seo?.pageSeo?.title || newsPage.pageHero?.title,
      noIndex: newsPage?.seo?.pageSeo?.noIndex,
      schema: {
        type: 'CollectionPage',
        socials: newsPage?.seo?.socials,
        organisation: {
          name: newsPage?.seo?.sitewideSeo?.organisationName,
          legalName: newsPage?.seo?.sitewideSeo?.organisationLegalName,
          description: newsPage?.seo?.sitewideSeo?.organisationDescription,
          founderName: newsPage?.seo?.sitewideSeo?.organisationFounder,
          foundingDate: newsPage?.seo?.sitewideSeo?.organisationFoundingDate,
          numberOfEmployees: newsPage?.seo?.sitewideSeo?.organisationNumberOfEmployees,
          logo: newsPage?.seo?.sitewideSeo?.organisationLogo,
          slogan: newsPage?.seo?.sitewideSeo?.organisationSlogan,
        },
      },
    },
    params?.siteId
  );

  return {
    props: { ...properties, siteId: params?.siteId },
    revalidate: getRevalidateInterval(),
  };
};

export default NewsOverviewPage;
